// Column plan and time logic for the Run Center journal.
//
// Pure and framework-free on purpose: the responsive rules live in
// RUN_CENTER_COLUMN_META so tests can assert the exact column set per
// breakpoint without jsdom media queries. The page maps this spec onto AntD
// columns (passing `responsive` through) and renders the started-time via
// runStartedDisplay.
import type { RunCenterRun } from '../api/run-center.client.js';

export type RunColumnKey =
  | 'scenario'
  | 'deal'
  | 'contact'
  | 'status'
  | 'started'
  | 'finished'
  | 'scheduled'
  | 'attempts'
  | 'outcome'
  | 'approval'
  | 'actions';

/** Breakpoints a column needs to become visible (`undefined` = always). */
export interface RunCenterColumnSpec {
  key: RunColumnKey;
  responsive?: ReadonlyArray<'md' | 'lg'>;
}

/**
 * Mobile-first column plan: at xs only the scenario, its status and the
 * pending-approval action stay visible; identity and timing columns appear
 * from md, operational details from lg. The page forwards `responsive` to
 * AntD, which hides the columns below the breakpoint (and keeps any residual
 * overflow scrolling inside the table itself, never the document).
 */
export const RUN_CENTER_COLUMN_META: readonly RunCenterColumnSpec[] = [
  { key: 'scenario' },
  { key: 'deal', responsive: ['md'] },
  { key: 'contact', responsive: ['md'] },
  { key: 'status' },
  { key: 'started', responsive: ['md'] },
  { key: 'finished', responsive: ['md'] },
  { key: 'scheduled', responsive: ['lg'] },
  { key: 'attempts', responsive: ['lg'] },
  { key: 'outcome', responsive: ['lg'] },
  { key: 'approval' },
  { key: 'actions' },
];

export interface RunCenterBreakpoints {
  md: boolean;
  lg: boolean;
}

/** Pure visibility rule used by unit tests and documentation of the grid. */
export function visibleRunCenterColumns(breakpoints: RunCenterBreakpoints): RunColumnKey[] {
  return RUN_CENTER_COLUMN_META.filter(
    (spec) =>
      spec.responsive === undefined ||
      spec.responsive.every((breakpoint) => breakpoints[breakpoint]),
  ).map((spec) => spec.key);
}

export type RunStartedDisplay = { kind: 'not_started' } | { kind: 'at'; iso: string };

/**
 * "Запущен" shows when the worker actually claimed the run (`startedAt`).
 * A queued run has never been claimed — creation time under a "started"
 * heading would lie, so it is reported as not started instead.
 */
export function runStartedDisplay(run: Pick<RunCenterRun, 'startedAt'>): RunStartedDisplay {
  return run.startedAt === null ? { kind: 'not_started' } : { kind: 'at', iso: run.startedAt };
}
